import React, { useState, useRef } from 'react';
import { Upload, X, Image as ImageIcon, Loader2 } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * ImageUploader Component
 * Handles image upload to B2C CMS with preview
 * 
 * @param {string} category - Upload category: banners, testimonials, news, categories, logos, pages
 * @param {string} value - Current image URL
 * @param {function} onChange - Callback when image URL changes
 * @param {string} label - Label for the upload field
 * @param {string} className - Additional CSS classes
 */
function ImageUploader({ category = 'banners', value, onChange, label = 'Image', className = '' }) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const inputRef = useRef(null);

  const getAuthHeaders = () => ({
    headers: { 
      Authorization: `Bearer ${localStorage.getItem('token')}`,
      'Content-Type': 'multipart/form-data'
    }
  });

  const handleUpload = async (file) => {
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      setError('Please upload a valid image (JPG, PNG, WEBP, or GIF)');
      return;
    }

    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      setError('Image size must be less than 10MB');
      return;
    }

    setError('');
    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(
        `${API}/api/b2c/cms/upload/${category}`,
        formData,
        getAuthHeaders()
      );

      // Construct full URL for the image
      const imageUrl = `${API}${response.data.url}`;
      onChange(imageUrl);
    } catch (err) {
      console.error('Upload error:', err);
      setError(err.response?.data?.detail || 'Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      handleUpload(file);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleUpload(file);
    }
  };

  const handleClear = () => {
    onChange('');
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  const handleUrlInput = (e) => {
    onChange(e.target.value);
  };

  return (
    <div className={`space-y-2 ${className}`}>
      <label className="block text-sm font-medium text-gray-700">{label}</label>
      
      {/* Image Preview */}
      {value && (
        <div className="relative inline-block">
          <div className="w-full max-w-xs h-32 bg-gray-100 rounded-lg overflow-hidden border">
            <img 
              src={value} 
              alt="Preview" 
              className="w-full h-full object-cover"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          </div>
          <button
            type="button"
            onClick={handleClear}
            className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 shadow-lg"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Upload Area */}
      <div
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        className={`border-2 border-dashed rounded-lg p-4 text-center transition-colors ${
          dragActive 
            ? 'border-primary bg-primary/5' 
            : 'border-gray-300 hover:border-gray-400'
        }`}
      >
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/jpg,image/png,image/webp,image/gif"
          onChange={handleFileSelect}
          className="hidden"
          id={`image-upload-${category}-${label.replace(/\s/g, '-')}`}
        />
        
        {uploading ? (
          <div className="flex flex-col items-center gap-2 py-2">
            <Loader2 className="w-8 h-8 text-primary animate-spin" />
            <span className="text-sm text-gray-500">Uploading...</span>
          </div>
        ) : (
          <label
            htmlFor={`image-upload-${category}-${label.replace(/\s/g, '-')}`}
            className="cursor-pointer flex flex-col items-center gap-2 py-2"
          >
            <div className="p-2 bg-gray-100 rounded-full">
              <Upload className="w-5 h-5 text-gray-500" />
            </div>
            <div className="text-sm">
              <span className="text-primary font-medium">Click to upload</span>
              <span className="text-gray-500"> or drag and drop</span>
            </div>
            <span className="text-xs text-gray-400">PNG, JPG, WEBP, GIF up to 10MB</span>
          </label>
        )}
      </div>

      {/* Manual URL Input (fallback) */}
      <div className="flex items-center gap-2">
        <span className="text-xs text-gray-400">or paste URL:</span>
        <input
          type="url"
          value={value || ''}
          onChange={handleUrlInput}
          placeholder="https://..."
          className="flex-1 px-3 py-1.5 text-sm border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
        />
      </div>

      {/* Error Message */}
      {error && (
        <p className="text-sm text-red-500 flex items-center gap-1">
          <X className="w-4 h-4" />
          {error}
        </p>
      )}
    </div>
  );
}

export default ImageUploader;
